import Physician from '@/types/Physician';
import { Patient, PatientStatus } from '@/types/Patient';
import { ProcedureType } from '@/types/Procedure';
import { Box, ButtonBase, SvgIcon, SvgIconProps, Typography } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import NotifyButton, { ChatButton } from '@/components/pages/kiosk/tablet/v2/NotifyButton';
import { Destination } from '@/types/NotificationTemplate';
import AnesthesiologistOr from '@/components/pages/kiosk/tablet/v2/AnethesiologistOr';
import Divider from '@material-ui/core/Divider';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { FlipRoom, useStyles } from '@/components/pages/kiosk/tablet/v2/ProcedureHandling';
import { eventTime } from '../../utils';
import { PROCEDURE_STATUSES } from '../../../../../entities/patient/enums';
import { Back } from '../../../pacuAndPrep/PatientHeader';

type ProcedureHeaderSectionProps = {
  isPreviousPatient: boolean;
  previousPatientExit?: () => void;
  status: string;
  physician?: Physician;
  patient?: Patient & { duration?: string };
  procedureType?: ProcedureType;
  editableAnesthesiologist?: boolean;
  notifyPhysician: () => Promise<void>;
  duration?: { duration?: string };
  enableFlipRoom: boolean;
  enableHelp: boolean;
  handleFlipRoom: () => Promise<void> | void;
  handleHelp: () => Promise<void> | void;
  isFlipRoomActive: boolean;
  isHelpActive: boolean;
};

export const ProcedureHeaderSection = ({
  isPreviousPatient,
  previousPatientExit,
  status,
  physician,
  patient,
  procedureType,
  editableAnesthesiologist,
  notifyPhysician,
  duration,
  enableFlipRoom,
  enableHelp,
  handleFlipRoom,
  handleHelp,
  isFlipRoomActive,
  isHelpActive,
}: ProcedureHeaderSectionProps) => {
  const classes = useStyles();

  return (
    <Box className={classes.header}>
      {isPreviousPatient && (
        <Back onClick={previousPatientExit}>
          <i className="material-icons">
            <ArrowBack fontSize={'large'} />
          </i>
        </Back>
      )}
      <Box className={classes.headerColumn}>
        <HeaderBlock
          title="Physician"
          name={physician?.name}
          action={
            status === PatientStatus.InOr && (
              <NotifyButton
                trigger="PatientReady"
                hasPhysician={Boolean(physician?.id)}
                destination={Destination.Physician}
                notify={notifyPhysician}
                profileMessagingEnabled={!!physician?.shouldSendNotification}
                hasPhoneNumber
                notified={!!eventTime(PROCEDURE_STATUSES.READY, patient)}
              />
            )
          }
          subtext={procedureType?.name}
        />
      </Box>
      <Box className={classes.headerColumn}>
        <HeaderBlock
          title="Anesthesiologist"
          name={patient?.procedure?.anesthesiologist?.name}
          action={
            !!patient?.procedure?.id && (
              <AnesthesiologistOr
                procedureId={patient?.procedure?.id}
                editableAnesthesiologist={editableAnesthesiologist}
              />
            )
          }
          subtext={`Anesthesia type ${patient?.procedure?.anesthesiaType || 'N/A'}`}
        />
      </Box>
      <Box className={classes.headerColumn}>
        <HeaderBlock
          title="Patient"
          name={patient?.name}
          action={<ChatButton patient={patient} />}
          subtext={`Duration ${patient?.duration || duration?.duration}`}
          aside={
            <Box display="flex" alignItems="stretch">
              <Divider orientation="vertical" />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                pl={2}
                pr={1}
                style={{ opacity: patient?.pacuBed ? undefined : 0.5 }}
              >
                <Typography variant="h3" style={{ fontWeight: 'bold' }}>
                  {patient?.pacuBed ?? '-'}
                </Typography>
                <div
                  className="material-icons"
                  style={{
                    color: 'inherit',
                  }}
                >
                  hotel
                </div>
              </Box>
            </Box>
          }
        />
      </Box>
      {!isPreviousPatient && (enableFlipRoom || enableHelp) && (
        <Box display="flex">
          {enableFlipRoom && (
            <ButtonBase
              onClick={handleFlipRoom}
              classes={{
                root: clsx(classes.flipRoomButton, {
                  [classes.flipRoomButtonActive]: isFlipRoomActive,
                }),
              }}
            >
              <FlipRoom style={{ color: 'white', width: 32, height: 32 }} />
              <Box>
                <Typography>Flip Room</Typography>
                {isFlipRoomActive && (
                  <Typography variant="caption" color="textSecondary">
                    Tap to turn off
                  </Typography>
                )}
              </Box>
            </ButtonBase>
          )}
          {enableHelp && (
            <ButtonBase
              onClick={handleHelp}
              classes={{
                root: clsx(classes.helpButton, {
                  [classes.helpButtonActive]: isHelpActive,
                }),
              }}
            >
              <PersonAlert style={{ color: 'white', width: 32, height: 32 }} />
              <div>
                <Typography>Help</Typography>
                {isHelpActive && (
                  <Typography variant="caption" style={{ opacity: 0.5 }}>
                    Tap to turn off
                  </Typography>
                )}
              </div>
            </ButtonBase>
          )}
        </Box>
      )}
    </Box>
  );
};

function PersonAlert(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M26.6667 14.6667C26.2889 14.6667 25.9722 14.5389 25.7167 14.2833C25.4611 14.0278 25.3333 13.7111 25.3333 13.3333C25.3333 12.9556 25.4611 12.6389 25.7167 12.3833C25.9722 12.1278 26.2889 12 26.6667 12C27.0445 12 27.3611 12.1278 27.6167 12.3833C27.8722 12.6389 28 12.9556 28 13.3333C28 13.7111 27.8722 14.0278 27.6167 14.2833C27.3611 14.5389 27.0445 14.6667 26.6667 14.6667ZM26.6667 10.6667C26.2889 10.6667 25.9722 10.5389 25.7167 10.2833C25.4611 10.0278 25.3333 9.71111 25.3333 9.33333V5.33333C25.3333 4.95556 25.4611 4.63889 25.7167 4.38333C25.9722 4.12778 26.2889 4 26.6667 4C27.0445 4 27.3611 4.12778 27.6167 4.38333C27.8722 4.63889 28 4.95556 28 5.33333V9.33333C28 9.71111 27.8722 10.0278 27.6167 10.2833C27.3611 10.5389 27.0445 10.6667 26.6667 10.6667ZM12 16C10.5333 16 9.27779 15.4778 8.23335 14.4333C7.1889 13.3889 6.66668 12.1333 6.66668 10.6667C6.66668 9.2 7.1889 7.94444 8.23335 6.9C9.27779 5.85556 10.5333 5.33333 12 5.33333C13.4667 5.33333 14.7222 5.85556 15.7667 6.9C16.8111 7.94444 17.3333 9.2 17.3333 10.6667C17.3333 12.1333 16.8111 13.3889 15.7667 14.4333C14.7222 15.4778 13.4667 16 12 16ZM1.33335 24V22.9333C1.33335 22.1778 1.52779 21.4833 1.91668 20.85C2.30557 20.2167 2.82224 19.7333 3.46668 19.4C4.84446 18.7111 6.24446 18.1944 7.66668 17.85C9.0889 17.5056 10.5333 17.3333 12 17.3333C13.4667 17.3333 14.9111 17.5056 16.3333 17.85C17.7556 18.1944 19.1556 18.7111 20.5333 19.4C21.1778 19.7333 21.6945 20.2167 22.0833 20.85C22.4722 21.4833 22.6667 22.1778 22.6667 22.9333V24C22.6667 24.7333 22.4056 25.3611 21.8833 25.8833C21.3611 26.4056 20.7333 26.6667 20 26.6667H4.00002C3.26668 26.6667 2.63891 26.4056 2.11668 25.8833C1.59446 25.3611 1.33335 24.7333 1.33335 24ZM4.00002 24H20V22.9333C20 22.6889 19.9389 22.4667 19.8167 22.2667C19.6945 22.0667 19.5333 21.9111 19.3333 21.8C18.1333 21.2 16.9222 20.75 15.7 20.45C14.4778 20.15 13.2445 20 12 20C10.7556 20 9.52224 20.15 8.30002 20.45C7.07779 20.75 5.86668 21.2 4.66668 21.8C4.46668 21.9111 4.30557 22.0667 4.18335 22.2667C4.06113 22.4667 4.00002 22.6889 4.00002 22.9333V24ZM12 13.3333C12.7333 13.3333 13.3611 13.0722 13.8833 12.55C14.4056 12.0278 14.6667 11.4 14.6667 10.6667C14.6667 9.93333 14.4056 9.30555 13.8833 8.78333C13.3611 8.26111 12.7333 8 12 8C11.2667 8 10.6389 8.26111 10.1167 8.78333C9.59446 9.30555 9.33335 9.93333 9.33335 10.6667C9.33335 11.4 9.59446 12.0278 10.1167 12.55C10.6389 13.0722 11.2667 13.3333 12 13.3333Z"
          fill="white"
        />
      </svg>
    </SvgIcon>
  );
}

const HeaderBlock = ({
  title,
  name,
  action,
  subtext,
  aside,
}: {
  title: string;
  name?: string;
  action?: ReactNode;
  subtext?: string;
  aside?: ReactNode;
}) => {
  const classes = useStyles();

  return (
    <Box display="flex" alignItems="stretch">
      <Box flex={1} display="flex" flexDirection="column" alignItems="stretch" maxWidth="100%">
        <Typography color="textSecondary" variant="h5">
          {title}
        </Typography>
        <Box className={classes.mainValue}>
          <Typography variant="h3" style={{ fontWeight: 'bold' }}>
            {name}
          </Typography>
          {action}
        </Box>
        <Typography className={classes.subtext}>{subtext}</Typography>
      </Box>
      {aside}
    </Box>
  );
};
